import Tooltip from '@/sharedComponents/TextEditor/components/Tooltip';
import { Text } from '@typedream/ui';
import classNames from 'classnames';
import Link from 'next/link';
import { useCallback } from 'react';
import style from './style.module.css';
import { MenuNavProps } from './types';

export function MenuNav({
  className,
  disabled,
  pre,
  href,
  post,
  selected,
  tooltip,
  onClick,
  children,
}: MenuNavProps) {
  const withTooltip = useCallback(
    (reactNode: React.ReactNode) => {
      if (tooltip) {
        return <Tooltip center={tooltip}>{reactNode}</Tooltip>;
      }
      return <>{reactNode}</>;
    },
    [tooltip]
  );

  const withHref = useCallback(
    (reactNode: React.ReactNode) => {
      const props = {
        className: 'flex-1 overflow-hidden text-ellipsis cursor-pointer',
        onClick,
        'data-testid': 'SidebarItem_container',
      };

      if (href) {
        return (
          <Link href={href} {...props}>
            {reactNode}
          </Link>
        );
      }

      return (
        <div role="button" onKeyDown={onClick} tabIndex={0} {...props}>
          {reactNode}
        </div>
      );
    },
    [className, href, onClick]
  );

  return (
    <div
      className={classNames(
        style.menu,
        'flex items-center gap-1.5 w-full rounded-md p-1.5 text-primary-soft',
        {
          'bg-background-highlight': selected,
          'hover:bg-background-highlight': !disabled,
        },
        className
      )}
    >
      {pre && <div className="flex items-center justify-center h-5 w-5 text-secondary">{pre}</div>}
      <div className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
        {withTooltip(
          withHref(
            <Text
              as="p"
              size="2xs"
              weight="medium"
              variant="primary-soft"
              className="w-auto whitespace-nowrap overflow-hidden text-ellipsis"
            >
              {children}
            </Text>
          )
        )}
      </div>
      {post && (
        <div
          className={classNames(
            style.post,
            'flex items-center justify-center text-secondary transition-all ease-out duration-200'
          )}
        >
          {post}
        </div>
      )}
    </div>
  );
}
